import { useI18n } from '../../contexts/I18nContext';
import { AlertTriangle } from 'lucide-react';

const LegalTermsPage = () => {
  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-background pt-20">
      <section className="section-spacing">
        <div className="container-app">
          <div className="max-w-3xl mx-auto">
            <p className="caption mb-4">Legal</p>
            <h1 className="heading-1 mb-8">Términos y Condiciones</h1>
            
            <div className="prose prose-invert max-w-none space-y-8">
              <div className="card-premium border-amber-500/20 bg-amber-500/5">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-sm text-muted-foreground">
                    Este documento establece los términos y condiciones de uso de la plataforma Silver Brick Development. 
                    Al utilizar nuestros servicios, usted acepta estos términos en su totalidad.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="heading-3 mb-4">1. Definiciones</h2>
                <p className="body-text">
                  "Plataforma" se refiere al sitio web y aplicación de Silver Brick Development. 
                  "Usuario" se refiere a cualquier persona que acceda o utilice la Plataforma. 
                  "Inversionista" se refiere a usuarios registrados que participan en oportunidades de inversión. 
                  "Aportador" se refiere a propietarios de inmuebles que contribuyen activos a la estructura.
                </p>
              </div>

              <div>
                <h2 className="heading-3 mb-4">2. Uso de la Plataforma</h2>
                <p className="body-text mb-4">
                  El acceso y uso de la Plataforma está sujeto a las siguientes condiciones:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>Debe ser mayor de edad según las leyes aplicables</li>
                  <li>Debe proporcionar información veraz y actualizada</li>
                  <li>Es responsable de mantener la confidencialidad de sus credenciales</li>
                  <li>Acepta no utilizar la Plataforma para fines ilegales o no autorizados</li>
                </ul>
              </div>

              <div>
                <h2 className="heading-3 mb-4">3. Información y Riesgos</h2>
                <p className="body-text">
                  La información presentada en la Plataforma es únicamente para fines informativos y no constituye 
                  asesoramiento de inversión, legal o fiscal. La inversión en bienes raíces conlleva riesgos 
                  significativos, incluyendo la posible pérdida total del capital invertido. Los rendimientos 
                  proyectados son estimaciones y no garantizan resultados futuros.
                </p>
              </div>

              <div>
                <h2 className="heading-3 mb-4">4. Proceso de Verificación</h2>
                <p className="body-text">
                  Para acceder a ciertas funciones de la Plataforma, los usuarios deben completar un proceso de 
                  verificación de identidad (KYC) y, en su caso, acreditación como inversionistas calificados. 
                  Silver Brick Development se reserva el derecho de rechazar usuarios que no cumplan con los 
                  requisitos establecidos.
                </p>
              </div>

              <div>
                <h2 className="heading-3 mb-4">5. Propiedad Intelectual</h2>
                <p className="body-text">
                  Todo el contenido de la Plataforma, incluyendo textos, gráficos, logotipos, y software, 
                  es propiedad de Silver Brick Development o sus licenciantes y está protegido por las leyes 
                  de propiedad intelectual aplicables.
                </p>
              </div>

              <div>
                <h2 className="heading-3 mb-4">6. Limitación de Responsabilidad</h2>
                <p className="body-text">
                  Silver Brick Development no será responsable por daños indirectos, incidentales, especiales 
                  o consecuentes que resulten del uso o la imposibilidad de usar la Plataforma.
                </p>
              </div>

              <div>
                <h2 className="heading-3 mb-4">7. Modificaciones</h2>
                <p className="body-text">
                  Silver Brick Development se reserva el derecho de modificar estos términos en cualquier momento. 
                  Los cambios serán efectivos al momento de su publicación en la Plataforma.
                </p>
              </div>

              <div>
                <h2 className="heading-3 mb-4">8. Ley Aplicable</h2>
                <p className="body-text">
                  Estos términos se regirán e interpretarán de acuerdo con las leyes de México. 
                  Cualquier disputa será sometida a la jurisdicción de los tribunales competentes en la Ciudad de México.
                </p>
              </div>

              <p className="text-sm text-muted-foreground pt-8 border-t border-border">
                Última actualización: Enero 2024
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const LegalPrivacyPage = () => {
  return (
    <div className="min-h-screen bg-background pt-20">
      <section className="section-spacing">
        <div className="container-app">
          <div className="max-w-3xl mx-auto">
            <p className="caption mb-4">Legal</p>
            <h1 className="heading-1 mb-8">Política de Privacidad</h1>
            
            <div className="prose prose-invert max-w-none space-y-8">
              <div>
                <h2 className="heading-3 mb-4">1. Información que Recopilamos</h2>
                <p className="body-text mb-4">
                  Recopilamos información que usted nos proporciona directamente, incluyendo:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>Datos de identificación personal (nombre, dirección, RFC)</li>
                  <li>Información de contacto (correo electrónico, teléfono)</li>
                  <li>Información financiera para cumplimiento KYC/AML</li>
                  <li>Documentos de identidad y comprobantes</li>
                  <li>Información de inversiones y transacciones</li>
                </ul>
              </div>

              <div>
                <h2 className="heading-3 mb-4">2. Uso de la Información</h2>
                <p className="body-text">
                  Utilizamos su información para: proveer nuestros servicios, cumplir con obligaciones 
                  legales y regulatorias, verificar su identidad, procesar transacciones, comunicarnos 
                  con usted, y mejorar nuestros servicios.
                </p>
              </div>

              <div>
                <h2 className="heading-3 mb-4">3. Compartición de Información</h2>
                <p className="body-text">
                  No vendemos su información personal. Podemos compartir información con: proveedores de 
                  servicios que nos ayudan a operar la plataforma, autoridades regulatorias cuando sea 
                  requerido por ley, y socios de inversión cuando sea necesario para completar transacciones.
                </p>
              </div>

              <div>
                <h2 className="heading-3 mb-4">4. Seguridad</h2>
                <p className="body-text">
                  Implementamos medidas de seguridad técnicas y organizativas para proteger su información 
                  contra acceso no autorizado, alteración, divulgación o destrucción.
                </p>
              </div>

              <div>
                <h2 className="heading-3 mb-4">5. Sus Derechos</h2>
                <p className="body-text">
                  Usted tiene derecho a acceder, rectificar, cancelar y oponerse al tratamiento de sus 
                  datos personales (derechos ARCO). Para ejercer estos derechos, contáctenos a través de 
                  los canales establecidos.
                </p>
              </div>

              <div>
                <h2 className="heading-3 mb-4">6. Retención de Datos</h2>
                <p className="body-text">
                  Conservamos su información durante el tiempo necesario para cumplir con los fines 
                  establecidos y las obligaciones legales aplicables.
                </p>
              </div>

              <p className="text-sm text-muted-foreground pt-8 border-t border-border">
                Última actualización: Enero 2024
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const LegalRiskDisclosuresPage = () => {
  return (
    <div className="min-h-screen bg-background pt-20">
      <section className="section-spacing">
        <div className="container-app">
          <div className="max-w-3xl mx-auto">
            <p className="caption mb-4">Legal</p>
            <h1 className="heading-1 mb-8">Avisos de Riesgo</h1>
            
            <div className="prose prose-invert max-w-none space-y-8">
              <div className="card-premium border-destructive/20 bg-destructive/5">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="font-medium text-destructive mb-2">Aviso Importante</p>
                    <p className="text-sm text-muted-foreground">
                      La inversión en bienes raíces conlleva riesgos significativos, incluyendo la posible 
                      pérdida total del capital invertido. Lea cuidadosamente los siguientes avisos antes de invertir.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="heading-3 mb-4">Riesgos Generales de Inversión</h2>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>Las inversiones inmobiliarias son ilíquidas y pueden requerir plazos largos</li>
                  <li>Los rendimientos proyectados son estimaciones y no están garantizados</li>
                  <li>El rendimiento pasado no es indicativo de resultados futuros</li>
                  <li>El valor de los activos puede fluctuar debido a condiciones de mercado</li>
                </ul>
              </div>

              <div>
                <h2 className="heading-3 mb-4">Riesgos de Mercado</h2>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>Fluctuaciones en el mercado inmobiliario</li>
                  <li>Cambios en tasas de interés</li>
                  <li>Variaciones en el tipo de cambio</li>
                  <li>Condiciones económicas generales</li>
                </ul>
              </div>

              <div>
                <h2 className="heading-3 mb-4">Riesgos de Proyecto</h2>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>Retrasos en construcción o desarrollo</li>
                  <li>Incremento en costos de materiales y mano de obra</li>
                  <li>Riesgo de vacancia</li>
                  <li>Problemas regulatorios o de permisos</li>
                </ul>
              </div>

              <div>
                <h2 className="heading-3 mb-4">Riesgos Legales y Regulatorios</h2>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>Cambios en leyes y regulaciones</li>
                  <li>Riesgos fiscales</li>
                  <li>Disputas legales</li>
                  <li>Cambios en políticas gubernamentales</li>
                </ul>
              </div>

              <div>
                <h2 className="heading-3 mb-4">No Constituye Oferta</h2>
                <p className="body-text">
                  La información presentada en esta plataforma no constituye una oferta pública de valores 
                  donde esté prohibido por las leyes aplicables. El acceso a ciertas oportunidades puede 
                  estar restringido según la jurisdicción y estatus de acreditación del inversionista.
                </p>
              </div>

              <div>
                <h2 className="heading-3 mb-4">Recomendación</h2>
                <p className="body-text">
                  Recomendamos encarecidamente consultar con asesores financieros, legales y fiscales 
                  independientes antes de tomar cualquier decisión de inversión. Solo invierta capital 
                  que pueda permitirse perder en su totalidad.
                </p>
              </div>

              <p className="text-sm text-muted-foreground pt-8 border-t border-border">
                Última actualización: Enero 2024
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export { LegalTermsPage, LegalPrivacyPage, LegalRiskDisclosuresPage };
